import axios from 'axios';

/**
 * LemonFox Speech-to-Text API service
 * Documentation: https://www.lemonfox.ai/apis/speech-to-text
 */

// Browser-compatible file system operations
const browserFS = {
  // Create a FormData object with the audio file
  createFormDataWithAudio: (audioBlob, filename) => {
    const formData = new FormData();
    formData.append('file', audioBlob, filename);
    return formData;
  },
  
  // Generate a path for the audio file
  joinPath: (directory, filename) => {
    return `${directory}/${filename}`;
  }
};
const LEMONFOX_API_URL = 'https://api.lemonfox.ai/v1';

/**
 * Create a new instance of axios with default configuration for LemonFox API
 */
const lemonFoxApi = axios.create({
  baseURL: LEMONFOX_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${process.env.REACT_APP_LEMONFOX_API_KEY || 'your-api-key-here'}`
  }
});

// /**
//  * Convert audio blob to base64 string
//  * @param {Blob} audioBlob - The audio blob to convert
//  * @returns {Promise<string>} - A promise that resolves to the base64 string
//  */
// const blobToBase64 = (audioBlob) => {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onloadend = () => {
//       // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
//       const base64String = reader.result.split(',')[1];
//       resolve(base64String);
//     };
//     reader.onerror = reject;
//     reader.readAsDataURL(audioBlob);
//   });
// };

/**
 * Transcribe audio using LemonFox Speech-to-Text API
 * @param {Blob} audioBlob - The audio blob to transcribe
 * @param {string} submissionId - The form submission ID
 * @param {number} partNumber - The question/part number
 * @returns {Promise<Object>} - A promise that resolves to the transcription result
 */
export const transcribeAudio = async (audioBlob, submissionId = 'temp', partNumber = 0) => {
  try {
    // Directory for audio files
    const directory = 'media/transcriptions';
    
    // Generate filename using the required format
    const filename = `temp-audio-${submissionId}-${partNumber}.mp3`;
    const filePath = browserFS.joinPath(directory, filename);
    
    console.log(`Saving audio to: ${filePath}`);
    
    // Create FormData with the audio file
    const formData = browserFS.createFormDataWithAudio(audioBlob, filename);
    
    // Add additional parameters
    formData.append('model', 'lemonfox-speech-to-text');
    formData.append('response_format', 'verbose_json');
    formData.append('file_path', filePath);
    
    // Create custom headers for multipart/form-data
    const headers = {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${process.env.REACT_APP_LEMONFOX_API_KEY || 'your-api-key-here'}`
    };
    
    // Make the API request
    const response = await axios.post(
      `${LEMONFOX_API_URL}/audio/transcriptions`,
      formData,
      { headers }
    );
    
    // Return the transcription result
    return response.data;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
};

/**
 * Mock transcription for testing without making actual API calls
 * @param {Blob} audioBlob - The audio blob to transcribe
 * @returns {Promise<Object>} - A promise that resolves to a mock transcription result
 */
export const mockTranscribeAudio = async (audioBlob, submissionId = 'temp', partNumber = 0) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Generate filename using the required format (for logging purposes)
  const directory = 'media/transcriptions';
  const filename = `temp-audio-${submissionId}-${partNumber}.mp3`;
  const filePath = browserFS.joinPath(directory, filename);
  
  console.log(`[MOCK] Would save audio to: ${filePath}`);
  
  // Return mock data in the format of LemonFox API with verbose_json
  return {
    text: "This is a mock transcription of the audio recording.",
    segments: [
      {
        id: 0,
        start: 0,
        end: 3.5,
        text: "This is a mock",
        confidence: 0.98
      },
      {
        id: 1,
        start: 3.5,
        end: 6.2,
        text: "transcription of the audio recording.",
        confidence: 0.95
      }
    ],
    language: "en",
    duration: 6.2,
    words: [
      { word: "This", start: 0.0, end: 0.5, confidence: 0.99 },
      { word: "is", start: 0.5, end: 0.8, confidence: 0.99 },
      { word: "a", start: 0.8, end: 1.0, confidence: 0.99 },
      { word: "mock", start: 1.0, end: 1.5, confidence: 0.97 },
      { word: "transcription", start: 3.5, end: 4.5, confidence: 0.96 },
      { word: "of", start: 4.5, end: 4.8, confidence: 0.99 },
      { word: "the", start: 4.8, end: 5.0, confidence: 0.99 },
      { word: "audio", start: 5.0, end: 5.5, confidence: 0.95 },
      { word: "recording", start: 5.5, end: 6.2, confidence: 0.94 }
    ],
    file_path: filePath
  };
};

// Use mock or real API based on environment
// const isTestMode = process.env.REACT_APP_TEST_MODE === 'false';
// export const speechToText = isTestMode ? mockTranscribeAudio : transcribeAudio;
export const speechToText = transcribeAudio;

export default lemonFoxApi;